import { useEffect, RefObject } from 'react';

export interface KeyboardControlledStore {
    highlightNextItem: (event: { source: 'keyboard' | 'mouse' }) => void;
    highlightPreviousItem: (event: { source: 'keyboard' | 'mouse' }) => void;
    selectHighlightedItem: () => void;
}

/**
 * Allows the user to traverse and select the items within a store via the keyboard.
 */
export function useKeyboardNavigation(
    ref: RefObject<HTMLElement>,
    store: KeyboardControlledStore,
    onKeyboardSelectsLocation?: () => void
): void {
    useEffect(() => {
        const refCleanup = ref.current;
        function updateHighlightedResult(keyEvent: KeyboardEvent): void {
            switch (keyEvent.key) {
                case 'ArrowDown':
                    store.highlightNextItem({ source: 'keyboard' });
                    break;
                case 'ArrowUp':
                    store.highlightPreviousItem({ source: 'keyboard' });
                    break;
                case 'Enter': {
                    store.selectHighlightedItem();
                    onKeyboardSelectsLocation?.();
                    break;
                }
            }
        }

        if (ref.current) {
            // Use instead of an event listener to deal with portalled components and keyboard event bubbling issues.
            ref.current.onkeydown = updateHighlightedResult;
        }
        return () => refCleanup?.removeEventListener('keydown', updateHighlightedResult);
    }, [onKeyboardSelectsLocation, ref, store]);
}
